import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CheckSquare, Plus, Search, Pencil, Trash2, Clock, Store, ChevronLeft, ChevronRight,
  CircleCheck, Circle, PauseCircle, BarChart3,
} from "lucide-react";
import { listTodos, deleteTodo } from "../../api/todos";
import { listBranches } from "../../api/branches";
import { listEmployees } from "../../api/employees";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Card, Button, Input, Select, EmptyState, TableSkeleton, ConfirmDialog, Avatar } from "../../components/ui";
import { PriorityChip } from "../../components/Chips";
import Pagination from "../../components/Pagination";
import TodoFormModal from "./TodoFormModal";
import { ActiveFilters } from "../../components/Filters";
import { unwrapList, refLabel, formatTime, formatClock, formatRate, errorMessage, todayISO } from "../../lib/records";
import { TODO_PRIORITIES, TODO_STATUSES, TODO_STATUS_STYLES } from "../../lib/enums";

const PAGE_SIZE = 10;

// Shifts a YYYY-MM-DD string by n days.
function shiftDate(date, n) {
  const d = new Date(date + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function dayLabel(date) {
  const today = todayISO();
  if (date === today) return "Today";
  if (date === shiftDate(today, -1)) return "Yesterday";
  return new Date(date + "T00:00:00").toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}

export default function TodosList() {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";

  // Which day's tick state is being shown. The API attaches per-day state to
  // every row (§9.2), so changing this re-renders the whole board.
  const [searchParams, setSearchParams] = useSearchParams();
  const [date, setDate] = useState(todayISO());
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: PAGE_SIZE, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [employeeId, setEmployeeId] = useState(() => searchParams.get("employee") || "");
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!isChain) return;
    listBranches({ limit: 100 })
      .then((res) => setBranches(unwrapList(res, 100).items))
      .catch(() => setBranches([]));
  }, [isChain]);

  useEffect(() => {
    listEmployees({ limit: 100, status: "ACTIVE", ...(isChain && branchId ? { partner: branchId } : {}) })
      .then((res) => setEmployees(unwrapList(res, 100).items))
      .catch(() => setEmployees([]));
  }, [branchId, isChain]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await listTodos({
        page,
        limit: PAGE_SIZE,
        date,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...(priority ? { priority } : {}),
        ...(employeeId ? { employee: employeeId } : {}),
        ...(isChain && branchId ? { partner: branchId } : {}),
      });
      const { items: list, pagination: meta } = unwrapList(res, PAGE_SIZE);
      setItems(list);
      setPagination(meta);
      setSummary(res?.data?.summary ?? null);
    } catch (err) {
      setItems([]);
      setSummary(null);
      setLoadError(errorMessage(err, "Tasks could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [page, date, search, status, priority, employeeId, branchId, isChain]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [date, search, status, priority, employeeId, branchId]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (employeeId) next.set("employee", employeeId);
    else next.delete("employee");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  async function confirmDelete() {
    setDeleting(true);
    try {
      await deleteTodo(deleteTarget._id);
      setDeleteTarget(null);
      await fetchData();
    } catch (err) {
      setLoadError(errorMessage(err, "That task could not be deleted."));
    } finally {
      setDeleting(false);
    }
  }

  const hasFilters = !!(search || status || priority || employeeId || branchId);
  const isToday = date === todayISO();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
        <PageHeader
          title="Daily tasks"
          subtitle="Recurring work assigned to your staff. The list resets itself every night."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Link to="/todos/stats">
                <Button
                  variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 text-sm px-3 py-1.5"
                >
                  <BarChart3 size={15} className="hidden sm:inline mr-1" /> Stats
                </Button>
              </Link>
              <Button
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
                className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 text-sm px-3 sm:px-4 py-1.5 sm:py-2"
              >
                <Plus size={16} className="hidden sm:inline mr-1" /> New task
              </Button>
            </div>
          }
        />

        {/* Day picker. Completion is per-day, so which day you are looking at
            is the single most important control on this page. */}
        <Card className="mb-4 sm:mb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 sm:p-4 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-lg shadow-emerald-100/40">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDate(shiftDate(date, -1))}
              className="rounded-md p-1.5 text-gray-500 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
              title="Previous day"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="text-center min-w-[7rem]">
              <p className="text-sm font-semibold text-gray-800">{dayLabel(date)}</p>
              <input
                type="date"
                value={date}
                max={todayISO()}
                onChange={(e) => e.target.value && setDate(e.target.value)}
                className="mt-0.5 w-full cursor-pointer border-0 bg-transparent p-0 text-center text-xs text-gray-500 outline-none"
              />
            </div>
            <button
              onClick={() => setDate(shiftDate(date, 1))}
              disabled={isToday}
              className="rounded-md p-1.5 text-gray-500 hover:bg-emerald-50 hover:text-emerald-700 transition-colors disabled:opacity-30 disabled:pointer-events-none"
              title="Next day"
            >
              <ChevronRight size={18} />
            </button>
            {!isToday && (
              <button
                onClick={() => setDate(todayISO())}
                className="ml-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
              >
                Jump to today
              </button>
            )}
          </div>

          {summary && (
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm">
              <span className="flex items-center gap-1.5 text-emerald-700">
                <CircleCheck size={15} />
                <strong>{summary.done ?? 0}</strong> done
              </span>
              <span className="flex items-center gap-1.5 text-gray-500">
                <Circle size={15} />
                <strong>{summary.pending ?? 0}</strong> pending
              </span>
              {summary.completionRate != null && (
                <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">
                  {formatRate(summary.completionRate)}
                </span>
              )}
            </div>
          )}
        </Card>

        <Card className="bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50 overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-emerald-100/60 p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search title or description…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="flex-1 sm:flex-none sm:w-32 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                >
                  <option value="">All tasks</option>
                  {TODO_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s === "ACTIVE" ? "Active" : "Paused"}
                    </option>
                  ))}
                </Select>
                <Select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="flex-1 sm:flex-none sm:w-32 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                >
                  <option value="">All priorities</option>
                  {TODO_PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              {isChain && (
                <div className="flex flex-1 items-center gap-2">
                  <Store size={14} className="shrink-0 text-gray-400" />
                  <Select
                    value={branchId}
                    onChange={(e) => {
                      setBranchId(e.target.value);
                      setEmployeeId("");
                    }}
                    className="w-full border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                  >
                    <option value="">All branches</option>
                    {branches.map((b) => (
                      <option key={b._id} value={b._id}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              <Select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                className="flex-1 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              >
                <option value="">Everyone</option>
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.name}
                  </option>
                ))}
              </Select>
            </div>

            <ActiveFilters
              resultCount={loading ? null : pagination.total}
              filters={[
                { label: "Search", value: search, onClear: () => setSearch("") },
                {
                  label: "Task",
                  value: status === "ACTIVE" ? "Active" : status === "PAUSED" ? "Paused" : "",
                  onClear: () => setStatus(""),
                },
                { label: "Priority", value: priority, onClear: () => setPriority("") },
                {
                  label: "Assignee",
                  value: employees.find((e) => e._id === employeeId)?.name,
                  onClear: () => setEmployeeId(""),
                },
                {
                  label: "Branch",
                  value: branches.find((b) => b._id === branchId)?.name,
                  onClear: () => setBranchId(""),
                },
              ]}
              onClearAll={() => {
                setSearch("");
                setStatus("");
                setPriority("");
                setEmployeeId("");
                setBranchId("");
              }}
            />
          </div>

          {loadError && !loading && (
            <div className="border-b border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
              {loadError}
            </div>
          )}

          {loading ? (
            <TableSkeleton rows={6} cols={5} />
          ) : items.length === 0 ? (
            <div className="p-8">
              <EmptyState
                icon={CheckSquare}
                title="No tasks for this day"
                description={
                  hasFilters
                    ? "No tasks match these filters. Try widening them."
                    : "Create a daily task and it will appear on your team's list every working day."
                }
                action={
                  !hasFilters && (
                    <Button
                      onClick={() => {
                        setEditing(null);
                        setFormOpen(true);
                      }}
                      className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25"
                    >
                      <Plus size={16} /> New task
                    </Button>
                  )
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left">
                <thead>
                  <tr className="text-xs sm:text-label-sm uppercase tracking-wide text-gray-500 border-b border-emerald-100">
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold w-28">{dayLabel(date)}</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Task</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Assignee</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold hidden sm:table-cell">Priority</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold hidden md:table-cell">Due by</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 text-right w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-100/50">
                  {items.map((todo) => {
                    const paused = todo.status === "PAUSED";
                    return (
                      <tr
                        key={todo._id}
                        className="group text-xs sm:text-body-sm text-gray-800 hover:bg-emerald-50/50 transition-colors duration-200"
                      >
                        {/* Read-only tick state. Only the employee can mark a
                            task done, from their own app — there is no
                            partner-side completion endpoint. */}
                        <td className="px-3 sm:px-4 py-2 sm:py-3">
                          {paused ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400">
                              <PauseCircle size={15} /> Paused
                            </span>
                          ) : todo.isDone ? (
                            <span
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700"
                              title={todo.completedAt ? `Marked at ${formatTime(todo.completedAt)}` : undefined}
                            >
                              <CircleCheck size={15} /> Done
                              {todo.completedAt && (
                                <span className="font-normal text-gray-400">{formatTime(todo.completedAt)}</span>
                              )}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400">
                              <Circle size={15} /> Pending
                            </span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3">
                          <p className={`font-medium text-xs sm:text-sm ${paused ? "text-gray-400" : "text-gray-800"}`}>
                            {todo.title}
                          </p>
                          {todo.description && (
                            <p className="text-xs text-gray-500 line-clamp-1">{todo.description}</p>
                          )}
                          {todo.completionNote && (
                            <p className="mt-0.5 text-xs italic text-emerald-700 line-clamp-1">
                              “{todo.completionNote}”
                            </p>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3">
                          <div className="flex items-center gap-2">
                            <Avatar name={refLabel(todo.employee, "?")} size={26} />
                            <div className="min-w-0">
                              <p className="truncate text-xs font-medium text-gray-700">
                                {refLabel(todo.employee, "Unassigned")}
                              </p>
                              {todo.employee?.designation && (
                                <p className="truncate text-xs text-gray-500">{todo.employee.designation}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3 hidden sm:table-cell">
                          <PriorityChip priority={todo.priority} />
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3 hidden md:table-cell text-gray-600">
                          {todo.dueTime ? (
                            <span className="flex items-center gap-1.5">
                              <Clock size={13} className="text-gray-400" />
                              {formatClock(todo.dueTime)}
                            </span>
                          ) : (
                            <span className="text-gray-400">Any time</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3">
                          <div className="flex items-center justify-end gap-1">
                            <span
                              className={`mr-1 hidden rounded-full px-2 py-0.5 text-[10px] font-semibold lg:inline ${
                                TODO_STATUS_STYLES[todo.status] ?? "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {todo.status === "ACTIVE" ? "Active" : "Paused"}
                            </span>
                            <button
                              onClick={() => {
                                setEditing(todo);
                                setFormOpen(true);
                              }}
                              title="Edit task"
                              className="rounded-md p-1.5 text-gray-400 hover:bg-emerald-50 hover:text-emerald-700 transition-colors duration-200"
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(todo)}
                              title="Delete task"
                              className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors duration-200"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && items.length > 0 && (
            <div className="px-3 sm:px-4 border-t border-emerald-100/60 pt-3 sm:pt-4">
              <Pagination
                page={pagination.page}
                pages={pagination.pages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={setPage}
              />
            </div>
          )}
        </Card>

        <TodoFormModal
          open={formOpen}
          onClose={() => setFormOpen(false)}
          todo={editing}
          defaultEmployeeId={employeeId}
          onSaved={() => {
            setFormOpen(false);
            fetchData();
          }}
        />

        <ConfirmDialog
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
          loading={deleting}
          danger
          title="Delete this task?"
          description={`"${deleteTarget?.title ?? "This task"}" will stop appearing on the daily list. Its completion history is kept.`}
          confirmLabel="Delete"
        />
      </div>
    </div>
  );
}
